import Sentry from '../sentry';

const CLEARFEED_CLIENT_ID = '27324619-ffed-4344-ba73-13d69bb6c5c1';

declare global {
  interface Window {
    ClearFeed?: (action: string, config: Record<string, unknown>) => void;
  }
}

let isLoaded = false;
let isInitialized = false;

const load = (onReady?: () => void) => {
  if (isLoaded || document.getElementById('cf-widget-script')) {
    isLoaded = true;
    onReady?.();
    return;
  }

  const script = document.createElement('script');
  script.id = 'cf-widget-script';
  script.src = 'https://cdn.clearfeed.app/chat-widget.js';
  script.async = true;
  script.onload = () => {
    isLoaded = true;
    onReady?.();
  };
  script.onerror = (event) => {
    const err = new Error('[ClearFeed] Failed to load chat widget script');
    console.error(err.message, event);
    Sentry.captureException(err, {
      tags: { service: 'clearfeed', phase: 'script-load' },
      extra: { src: script.src },
    });
  };
  document.body.appendChild(script);
};

const init = (user?: { name: string; email: string; hash?: string }) => {
  if (!isLoaded || !window.ClearFeed) {
    console.warn('[ClearFeed] Script not loaded yet.');
    return;
  }
  if (isInitialized) return;

  try {
    // Only pass the user block when we have an HMAC hash — ClearFeed treats unhashed
    // user data as spoofable and we'd rather fall back to anonymous than send unverified
    // identity claims.
    const verifiedUser =
      user?.hash && user.name && user.email
        ? { name: user.name, email: user.email, hash: user.hash }
        : undefined;

    window.ClearFeed('init', {
      client_id: CLEARFEED_CLIENT_ID,
      user: verifiedUser,
      config: {
        brand_color: '#1f7a1f',
        icon_logo_color: '#ffffff',
        icon_type: 'round',
        icon_size: 'medium',
        position: 'right',
      },
    });
    isInitialized = true;
  } catch (e) {
    console.error('[ClearFeed] Failed to initialize:', e);
    Sentry.captureException(e, {
      tags: { service: 'clearfeed', phase: 'init' },
    });
  }
};

const boot = (user?: { name: string; email: string; hash?: string }) => {
  load(() => init(user));
};

let prefillObserver: MutationObserver | null = null;

const setPrefillValue = (textarea: HTMLTextAreaElement, text: string) => {
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
    window.HTMLTextAreaElement.prototype,
    'value'
  )?.set;
  nativeInputValueSetter?.call(textarea, `${text}\n`);
  textarea.dispatchEvent(new Event('input', { bubbles: true }));
  textarea.focus();
};

const watchForTextarea = (text: string) => {
  prefillObserver?.disconnect();

  const root = document.getElementById('clearfeed-root');
  if (!root) return;

  // Track the exact DOM node we last prefilled + whether we've ever reached the
  // chat screen in this watch session. Combined, this lets us:
  //   - On first open, auto-click "Start a new conversation" to get from the
  //     list / empty-state onto the chat screen.
  //   - While the same textarea is on screen, respect user edits (never re-fill
  //     the same node).
  //   - If the chat screen remounts with a fresh textarea (e.g. a slow-network
  //     retry where the user manually re-clicks the new-convo button), refill.
  //   - Once the user has been on the chat screen, DO NOT auto-yank them back to
  //     it when they navigate to the conversation list — respect the back action.
  let lastFilledTextarea: HTMLTextAreaElement | null = null;
  let hasReachedChatScreen = false;

  const tryPrefill = () => {
    const textarea = document.getElementById('message-textarea') as HTMLTextAreaElement | null;
    if (!textarea) {
      lastFilledTextarea = null;
      // Only auto-click "Start a new conversation" BEFORE we've ever reached the
      // chat screen. After that, the user owns their own navigation.
      if (!hasReachedChatScreen) {
        const newBtn = findNewConversationButton(root);
        if (newBtn) {
          robustClick(newBtn);
        }
      }
      return;
    }
    // Same DOM node as last fill → the user is editing; leave them alone.
    if (textarea === lastFilledTextarea) return;
    // Different node (or first sighting) → fill it, then remember the reference.
    if (!textarea.value.trim()) {
      setPrefillValue(textarea, text);
    }
    lastFilledTextarea = textarea;
    hasReachedChatScreen = true;
  };

  tryPrefill();

  prefillObserver = new MutationObserver(() => tryPrefill());
  prefillObserver.observe(root, { childList: true, subtree: true });
};

let closeBtnRef: HTMLButtonElement | null = null;

const stopWatching = () => {
  prefillObserver?.disconnect();
  prefillObserver = null;
  closeBtnRef?.removeEventListener('click', stopWatching);
  closeBtnRef = null;
};

const listenForClose = () => {
  const root = document.getElementById('clearfeed-root');
  closeBtnRef = root?.querySelector<HTMLButtonElement>('button[aria-label="Close chat"]') ?? null;
  closeBtnRef?.addEventListener('click', stopWatching, { once: true });
};

const open = (prefill?: string) => {
  if (!isInitialized) {
    console.warn('[ClearFeed] Widget not initialized yet.');
    return;
  }
  try {
    window.ClearFeed?.('open', {});
    if (prefill) {
      setTimeout(() => {
        watchForTextarea(prefill);
        listenForClose();
      }, 300);
    }
  } catch (e) {
    console.error('[ClearFeed] Failed to open widget:', e);
  }
};

const findSendButton = (root: HTMLElement) =>
  root.querySelector<HTMLButtonElement>(
    'button[aria-label*="Send" i]:not([disabled]), button[type="submit"]:not([disabled])'
  );

const findNewConversationButton = (root: HTMLElement) => {
  const buttons = root.querySelectorAll<HTMLButtonElement>('button:not([disabled])');
  for (const btn of Array.from(buttons)) {
    const text = btn.textContent?.trim().toLowerCase() ?? '';
    if (text.includes('new conversation') || text.includes('start a new conversation')) {
      return btn;
    }
  }
  return null;
};

// ClearFeed buttons sometimes ignore a bare .click() — dispatch a full pointer sequence
// so React handlers pick it up reliably (matters for the "Start a new conversation" button
// on the empty-state "No conversations yet" screen that first-time users land on).
const robustClick = (el: HTMLElement) => {
  const opts = { bubbles: true, cancelable: true, view: window };
  el.dispatchEvent(new PointerEvent('pointerdown', opts));
  el.dispatchEvent(new MouseEvent('mousedown', opts));
  el.dispatchEvent(new PointerEvent('pointerup', opts));
  el.dispatchEvent(new MouseEvent('mouseup', opts));
  el.click();
};

const HIDE_STYLE_ID = 'cf-hide-during-send';

const hidePanelDuringSend = () => {
  if (document.getElementById(HIDE_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = HIDE_STYLE_ID;
  // Hide only the chat panel (not the close-chat launcher) while we navigate to the
  // new-conversation screen in the background. visibility (not display:none) so children
  // still mount and the "Start a new conversation" button is clickable.
  style.textContent =
    '#clearfeed-root .cf-fixed.cf-overflow-hidden.cf-flex-col{visibility:hidden!important;}';
  document.head.appendChild(style);
};

const revealPanel = () => {
  document.getElementById(HIDE_STYLE_ID)?.remove();
};

const openAndSend = (text: string) => {
  if (!isInitialized) {
    console.warn('[ClearFeed] Widget not initialized yet.');
    return;
  }
  try {
    hidePanelDuringSend();
    window.ClearFeed?.('open', {});

    setTimeout(() => {
      const root = document.getElementById('clearfeed-root');
      if (!root) {
        revealPanel();
        return;
      }

      prefillObserver?.disconnect();
      let sent = false;

      const trySendFlow = () => {
        if (sent) return;

        // Keep trying to click "Start a new conversation" until the textarea actually
        // appears — the widget may expose this button on multiple screens (list view and
        // the empty-state "No conversations yet" screen that first-time users land on).
        // Re-clicking a transitional button is a no-op, so there's no need to latch.
        const textarea = document.getElementById('message-textarea') as HTMLTextAreaElement | null;
        if (!textarea) {
          const newBtn = findNewConversationButton(root);
          if (newBtn) {
            robustClick(newBtn);
          }
          return;
        }

        // Chat screen is now mounted — reveal the panel before we type + send so the
        // user sees a clean landing on the chat view instead of the list→chat transition.
        revealPanel();

        if (!textarea.value.trim()) {
          setPrefillValue(textarea, text);
        }
        // Small delay so React enables the send button after the input event
        setTimeout(() => {
          if (sent) return;
          const sendBtn = findSendButton(root);
          if (sendBtn) {
            robustClick(sendBtn);
            sent = true;
            prefillObserver?.disconnect();
            prefillObserver = null;
          }
        }, 100);
      };

      trySendFlow();
      prefillObserver = new MutationObserver(() => trySendFlow());
      prefillObserver.observe(root, { childList: true, subtree: true });

      setTimeout(() => {
        if (!sent) {
          prefillObserver?.disconnect();
          prefillObserver = null;
        }
        // Safety: always reveal, even if the flow stalled
        revealPanel();
      }, 30000);
    }, 300);
  } catch (e) {
    console.error('[ClearFeed] Failed to open widget and send:', e);
    revealPanel();
  }
};

const clearfeed = { load, init, boot, open, openAndSend };

export default clearfeed;
